import * as React from 'react';
import { IconButton as MuiIconButton } from '@mui/material';
import { IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';
import { useTheme } from '@emotion/react';
import capitalize from 'lodash/capitalize';

import { commonStyles, iconButtonVariantStyles } from './iconButtonCssDefs';

interface IconButtonProps
  extends Pick<MuiIconButtonProps, Exclude<keyof MuiIconButtonProps, 'color'>> {
  ['aria-label']: string;
  component?: React.ElementType;
  color?: 'danger' | 'primary' | 'secondary' | 'tertiary';
  href?: string;
  to?: string;
  variant?: 'contained' | 'outlined' | 'text';
}

type ClassKey =
  | 'containedPrimary'
  | 'containedSecondary'
  | 'containedDanger'
  | 'containedTertiary'
  | 'outlinedPrimary'
  | 'outlinedSecondary'
  | 'outlinedTertiary'
  | 'outlinedDanger'
  | 'textPrimary'
  | 'textSecondary'
  | 'textTertiary'
  | 'textDanger';

/**
 * A styled IconButton component that adds variants modeled after our standard Button.
 * Supported variants are "outlined", "contained" and "text" (default). To use different sizes
 * set the size prop on the icon itself. Supported sizes are "small", "default" and "large".
 *
 * @see {@link https://mui.com/api/icon-button/} Material API docs for props
 *
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { children, color = 'primary', variant = 'text', ...rest },
  ref
) {
  const theme = useTheme();
  const variantStyles = iconButtonVariantStyles(theme);
  const styleKey = `${variant}${capitalize(color)}` as ClassKey;

  return (
    <MuiIconButton css={[commonStyles(theme), variantStyles[styleKey]]} ref={ref} {...rest}>
      {children}
    </MuiIconButton>
  );
});

export default IconButton;
export type { IconButtonProps };
